import { memo, useCallback, useMemo } from 'react';
import { List, Paginator } from '@divlab/divanui';

import * as ModalWindows from '@Stores/ModalWindows';
import Review from '@Components/Review';
import useSearchParams from '@Hooks/useSearchParams';
import useNavigation from '@Navigation/useNavigation';
import styles from './ListReviews.module.css';

import type { PaginatorItem } from '@divlab/divanui';
import type { ReviewData } from '@Types/Review';
import type { FC, HTMLAttributes, MouseEvent } from 'react';

interface IndexesData {
  reviewIndex?: number;
  photoIndex?: number;
}

export interface ListReviewsProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  reviews?: ReviewData[];
  onChangePage?: (e: MouseEvent, page: number) => void;
  nowPage: number;
}

const countVisible = 5;
const ListReviews: FC<ListReviewsProps> = (props) => {
  const { reviews, onChangePage, nowPage, ...restProps } = props;
  const { pathname } = useNavigation();
  const searchParams = useSearchParams();
  const total = Math.ceil(reviews.length / countVisible);

  const visibleReviews = useMemo(() => {
    const start = (nowPage - 1) * countVisible;
    const end = nowPage * countVisible;

    return reviews.slice(start, end);
  }, [nowPage, reviews]);

  const handleChangePage = useCallback(
    (e: MouseEvent, { page }: PaginatorItem) => {
      if (page > 1) {
        searchParams.set('page', page.toString());
      } else {
        searchParams.delete('page');
      }

      window.history.pushState({}, null, `${pathname}?${searchParams.toString()}`);

      if (onChangePage) onChangePage(e, page);
    },
    [onChangePage, pathname, searchParams],
  );

  const getReviewAndPhotoIndexes = useCallback(
    (photoId: number) => {
      const result: IndexesData = {};

      reviews.some((item: ReviewData, itemIndex) => {
        const photoItemIndex = item.photos.findIndex((photo) => photo.id === photoId);
        if (photoItemIndex !== -1) {
          result.reviewIndex = itemIndex;
          result.photoIndex = photoItemIndex;
          return true;
        }

        return false;
      });

      return result;
    },
    [reviews],
  );

  const handleClickReviewImage = useCallback(
    (e, photoId: number) => {
      const indexes = getReviewAndPhotoIndexes(photoId);
      if (!indexes.reviewIndex && indexes.reviewIndex !== 0) return;

      ModalWindows.open('Review', { indexes, reviews });
    },
    [reviews, getReviewAndPhotoIndexes],
  );

  return (
    <div {...restProps}>
      <List
        className={styles.listReviews}
        items={visibleReviews}
        renderChild={(review: ReviewData) => (
          <Review className={styles.review} review={review} onClickImage={handleClickReviewImage} />
        )}
      />

      {total > 1 && (
        <div className={styles.wrapperNav}>
          <Paginator
            data-testid='feedback-paginator'
            now={nowPage}
            total={total}
            onChange={handleChangePage}
          />
        </div>
      )}
    </div>
  );
};

export default memo(ListReviews);
